import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleLabel: Record<string, string> = {
    superadmin: '超级管理员',
    admin: '管理员',
    teacher: '老师',
    student: '学生',
    parent: '家长',
  };

  // 根据角色定义导航菜单
  const navItems: { to: string; label: string; icon: string }[] = [];

  if (user?.role === 'superadmin') {
    navItems.push(
      { to: '/dashboard', label: '首页', icon: '🏠' },
      { to: '/homework', label: '作业管理', icon: '📝' },
      { to: '/feedback', label: '反馈管理', icon: '💬' },
      { to: '/attendance', label: '考勤管理', icon: '📋' },
      { to: '/calendar', label: '共享日历', icon: '📅' },
      { to: '/admin/invite-codes', label: '邀请码', icon: '🔑' },
      { to: '/admin/students', label: '学生管理', icon: '🎓' },
      { to: '/admin/users', label: '用户管理', icon: '👥' }
    );
  } else if (user?.role === 'admin') {
    navItems.push(
      { to: '/dashboard', label: '首页', icon: '🏠' },
      { to: '/homework', label: '作业管理', icon: '📝' },
      { to: '/feedback', label: '反馈管理', icon: '💬' },
      { to: '/attendance', label: '考勤管理', icon: '📋' },
      { to: '/calendar', label: '共享日历', icon: '📅' },
      { to: '/admin/students', label: '学生管理', icon: '🎓' }
    );
  } else if (user?.role === 'teacher') {
    navItems.push(
      { to: '/dashboard', label: '首页', icon: '🏠' },
      { to: '/homework', label: '作业管理', icon: '📝' },
      { to: '/feedback', label: '反馈管理', icon: '💬' },
      { to: '/attendance', label: '考勤管理', icon: '📋' },
      { to: '/calendar', label: '共享日历', icon: '📅' },
      { to: '/teacher/workbench', label: '上课看板', icon: '📋' },
      { to: '/admin/invite-codes', label: '邀请码', icon: '🔑' }
    );
  } else if (user?.role === 'student') {
    navItems.push(
      { to: '/dashboard', label: '首页', icon: '🏠' },
      { to: '/homework', label: '我的作业', icon: '📝' },
      { to: '/feedback', label: '我的反馈', icon: '💬' },
      { to: '/attendance', label: '我的考勤', icon: '📋' },
      { to: '/calendar', label: '共享日历', icon: '📅' }
    );
  } else if (user?.role === 'parent') {
    navItems.push(
      { to: '/dashboard', label: '首页', icon: '🏠' },
      { to: '/homework', label: '孩子的作业', icon: '📝' },
      { to: '/feedback', label: '孩子的反馈', icon: '💬' },
      { to: '/attendance', label: '孩子的考勤', icon: '📋' },
      { to: '/calendar', label: '共享日历', icon: '📅' }
    );
  }

  return (
    <div className="app-shell flex">
      {/* 侧边栏 - 桌面端 (md+) */}
      <aside className="glass-panel sticky top-0 hidden h-screen md:flex md:w-60 lg:w-72 md:flex-col rounded-r-[2rem]">
        <div className="p-5 lg:p-7 border-b border-white/60">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="ACE" className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl object-cover shadow-lg shadow-blue-900/10" />
            <div>
              <h1 className="text-xl lg:text-2xl font-semibold text-slate-950">ACE</h1>
              <p className="text-xs text-slate-500">Education Platform</p>
            </div>
          </div>
          <p className="mt-4 inline-flex rounded-full border border-white/70 bg-white/65 px-3 py-1 text-xs text-slate-600">
            {roleLabel[user?.role || 'student']} · {user?.name}
          </p>
        </div>
        <nav className="flex-1 p-3 lg:p-4 space-y-1 lg:space-y-1.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-2xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-slate-950 text-white shadow-xl shadow-slate-900/15'
                    : 'text-slate-600 hover:bg-white/70 hover:text-slate-950'
                }`
              }
            >
              <span className="text-lg">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 lg:p-4 border-t border-white/60">
          <button
            onClick={handleLogout}
            className="w-full rounded-2xl px-4 py-3 text-sm font-medium text-slate-500 transition hover:bg-white/70 hover:text-red-600"
          >
            🚪 退出登录
          </button>
        </div>
      </aside>

      {/* 移动端头部 ( < md ) */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 glass-panel px-4 py-3 flex items-center justify-between rounded-b-3xl">
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/70 text-slate-600 active:bg-slate-100 transition-colors"
          aria-label="打开菜单"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18"/>
          </svg>
        </button>
        <div className="flex items-center gap-2.5">
          <img src="/logo.jpg" alt="ACE" className="w-9 h-9 rounded-xl object-cover shadow-md" />
          <h1 className="text-lg font-semibold text-slate-950">ACE</h1>
        </div>
        {/* 占位保持居中 */}
        <div className="w-10" />
      </div>

      {/* 移动端侧边栏滑出 */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* 遮罩 */}
          <div
            className="absolute inset-0 bg-slate-950/30 backdrop-blur-sm transition-opacity"
            onClick={() => setSidebarOpen(false)}
          />
          {/* 侧边栏面板 */}
          <div className="glass-panel absolute left-0 top-0 bottom-0 w-72 rounded-r-[2rem] animate-slide-in-left flex flex-col">
            <div className="p-5 border-b border-white/60 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">
                  {roleLabel[user?.role || 'student']}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">{user?.name}</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
                aria-label="关闭菜单"
              >
                ✕
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/dashboard'}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-slate-950 text-white shadow-xl shadow-slate-900/15'
                        : 'text-slate-600 active:bg-white/70'
                    }`
                  }
                >
                  <span className="text-lg">{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-white/60">
              <button
                onClick={handleLogout}
                className="w-full rounded-2xl px-4 py-3 text-sm font-medium text-red-500 active:bg-white/70 transition-colors"
              >
                🚪 退出登录
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <main className="flex-1 overflow-auto min-h-screen">
        {/* mt-16 为移动端头部留空间，md+ 无需 */}
        <div className="p-3 sm:p-5 md:p-8 lg:p-10 mt-16 md:mt-0 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
